import { firebaseAuth } from '~/config/firebase.config';
import { STRIPE_SERVER } from '~/config/global.config';
import type { PausePlan } from './getSubscriptionStatus';

/**
 * Actions d'abonnement (pause, reprise, portail de résiliation) sur stripe-b2b.
 * Chaque appel porte le jeton Firebase de l'utilisateur : le serveur vérifie
 * qu'il est admin de l'établissement et lit lui-même le client Stripe dans
 * Firestore (on n'envoie jamais de customerId).
 */

export type SubscriptionErrorCode =
    | 'unauthenticated'
    | 'invalid_token'
    | 'forbidden'
    | 'client_not_found'
    | 'no_customer'
    | 'disabled'
    | 'invalid_months'
    | 'already_paused'
    | 'not_paused'
    | 'payment_failed'
    | 'trialing'
    | 'payment_issue'
    | 'recently_paused'
    | 'network'
    | string;

export class SubscriptionActionError extends Error {
    constructor(
        public code: SubscriptionErrorCode,
        /** Carte refusée à la reprise : portail Stripe « mettre à jour ma carte ». */
        public portalUrl?: string
    ) {
        super(code);
    }
}

/** Langue courte pour Stripe (« fr-FR » → « fr », cf gotcha #166). */
export const shortLang = (lang?: string) => (lang || 'en').split('-')[0].toLowerCase();

/** POST JSON authentifié vers stripe-b2b. Lève SubscriptionActionError. */
export async function postToStripeServer<T>(route: string, body: Record<string, unknown>): Promise<T> {
    const user = firebaseAuth.currentUser;
    if (!user) throw new SubscriptionActionError('unauthenticated');
    let response: Response;
    try {
        const idToken = await user.getIdToken();
        response = await fetch(`${STRIPE_SERVER}${route}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
            body: JSON.stringify(body),
        });
    } catch {
        throw new SubscriptionActionError('network');
    }
    let json: any = null;
    try {
        json = await response.json();
    } catch {
        /* réponse non JSON */
    }
    if (!response.ok) throw new SubscriptionActionError(json?.error || `http_${response.status}`, json?.portalUrl);
    return json as T;
}

export type PauseResult = {
    pause: { status: string; months: number; startsAt: number; resumesAt: number; endsAt: number } | null;
    emailSent: boolean;
    cancelAtPeriodEndRemoved?: boolean;
    /** Une pause était déjà en place (double clic, autre onglet) : c'est elle qui est renvoyée. */
    alreadyPaused?: boolean;
    /** Plan réellement posé par le serveur (null si alreadyPaused). */
    plan?: PausePlan | null;
};

/** Ce que la console a affiché au clic : le serveur refuse (409 phase_mismatch) si ça a changé. */
export type ResumeKind = 'cancel_before_start' | 'resume_early';

export type ResumeResult = { result: ResumeKind; status: string; cancelAtPeriodEndRestored?: boolean };

/** `expectedStartsAt` = début de pause affiché : 409 period_changed si l'échéance a bougé. */
export const pauseSubscription = (clientId: string, months: 1 | 2 | 3, expectedStartsAt?: number) =>
    postToStripeServer<PauseResult>('/subscription/pause', { clientId, months, expectedStartsAt });

export const resumeSubscription = (clientId: string, lang: string, expected: ResumeKind) =>
    postToStripeServer<ResumeResult>('/subscription/resume', { clientId, lang: shortLang(lang), expected });

/** URL du portail Stripe ouvert sur l'écran d'annulation (motifs Stripe). */
export const openCancelSession = (clientId: string, lang: string) =>
    postToStripeServer<{ url: string; flow: string }>('/subscription/cancel-session', {
        clientId,
        lang: shortLang(lang),
    });

export type KeepResult = {
    kept: boolean;
    /** Déjà gardé (double clic, autre onglet) : rien n'a changé. */
    alreadyKept?: boolean;
    status: string;
    trialEnd: number | null;
    currentPeriodEnd: number | null;
};

/** « Garder Unboared » : retire la résiliation programmée (rien n'est prélevé au clic). */
export const keepSubscription = (clientId: string, origin: 'account' | 'email') =>
    postToStripeServer<KeepResult>('/subscription/keep', { clientId, origin });
